import {
  useConfirmDialog,
  useExistingIntegrationNotices,
  useIntegrationRequestActions,
  useIntegrationRun,
  useIsIntegrationIt,
  useObservationDraft,
} from '../../hooks';
import { INTEGRATION_IT_ROLES, INTEGRATION_REQUESTS_PATH } from '../../config';
import RequestServicesCard from './RequestServicesCard';
import IntegrationDelivery from './IntegrationDelivery';
import RequestBranchesCard from './RequestBranchesCard';
import RequestSummaryCard from './RequestSummaryCard';
import RequestWebhookCard from './RequestWebhookCard';
import WebhookDelivery from './WebhookDelivery';
import ObservationPanel from './ObservationPanel';
import { useNavigate } from 'react-router-dom';
import IntegrationRun from './IntegrationRun';
import DetailActions from './DetailActions';
import DetailCard from './DetailCard';
import { Authorize } from '@/components';
import { ExistingIntegrationAlert } from '../ExistingIntegrationAlert';
import { useGetIntegrationRequestFormOptionsQuery } from '../../../../services';
import {
  EDITABLE_INTEGRATION_REQUEST_STATUSES,
  IntegrationRequestStatus,
  type IntegrationRequestDetail as IntegrationRequestDetailModel,
} from '../../../../models/IntegrationRequest';

interface IntegrationRequestDetailProps {
  request: IntegrationRequestDetailModel;
}

const DELIVERABLE_STATUSES = [
  IntegrationRequestStatus.INTEGRATED,
  IntegrationRequestStatus.DOC_APPROVED,
  IntegrationRequestStatus.SENT,
];
const DOC_VISIBLE_STATUSES = [IntegrationRequestStatus.DOC_APPROVED, IntegrationRequestStatus.SENT];

const IntegrationRequestDetail = ({ request }: IntegrationRequestDetailProps) => {
  const navigate = useNavigate();
  const isIt = useIsIntegrationIt();
  const prepare = request.result?.prepare;
  const canSeeDoc = isIt || DOC_VISIBLE_STATUSES.includes(request.status);
  const isDocInReview =
    request.type === 'API' && request.status === IntegrationRequestStatus.INTEGRATED;
  const confirmation = useConfirmDialog();
  const actions = useIntegrationRequestActions(request.id, confirmation.confirm);
  const observation = useObservationDraft();
  const run = useIntegrationRun(request);
  const hasIntegrationFailure = request.steps?.some((step) => step.state === 'failed') ?? false;
  const isEditable = EDITABLE_INTEGRATION_REQUEST_STATUSES.includes(request.status);
  const { data: formOptions } = useGetIntegrationRequestFormOptionsQuery(request.commerceId, {
    skip: !isEditable,
  });
  const notices = useExistingIntegrationNotices(isEditable ? formOptions : undefined, {
    type: request.type,
    logisticOperatorId: request.logisticOperatorId,
    serviceWithOperatorIds: request.serviceWithOperatorIds,
    webhookUrl: request.webhook?.url ?? '',
    webhookStatusIds: request.webhook?.statusIds ?? [],
    currentRequestId: request.id,
  });

  const submitObservation = async () => {
    if (await actions.observe(observation.text)) observation.close();
  };

  return (
    <>
      <ExistingIntegrationAlert notices={notices} />

      <RequestSummaryCard request={request} />

      {request.type === 'API' && (
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <RequestServicesCard services={request.services} />
          <RequestBranchesCard branches={request.branches} allBranches={request.allBranches} />
        </div>
      )}

      {request.webhook && (
        <RequestWebhookCard webhook={request.webhook} statuses={request.webhookStatuses} />
      )}

      {request.status !== IntegrationRequestStatus.CANCELLED && (
        <Authorize permission={INTEGRATION_IT_ROLES}>
          <IntegrationRun request={request} run={run} />
        </Authorize>
      )}

      {prepare && canSeeDoc && DELIVERABLE_STATUSES.includes(request.status) && (
        <IntegrationDelivery request={request} prepare={prepare} isInReview={isDocInReview} />
      )}

      {isDocInReview && !isIt && (
        <DetailCard title='Entrega al cliente'>
          <p className='text-sm text-gray-600'>
            La integración está lista. TI está revisando la documentación; cuando la apruebe, podrá
            descargarla y enviarla al cliente.
          </p>
        </DetailCard>
      )}

      {request.type === 'WEBHOOK' &&
        request.webhook &&
        DELIVERABLE_STATUSES.includes(request.status) && (
          <WebhookDelivery request={request} webhook={request.webhook} />
        )}

      {observation.isOpen && (
        <Authorize permission={INTEGRATION_IT_ROLES}>
          <ObservationPanel
            text={observation.text}
            isSaving={actions.isObserving}
            disabled={actions.isBusy}
            onChange={observation.setText}
            onCancel={observation.close}
            onSubmit={submitObservation}
          />
        </Authorize>
      )}

      <DetailActions
        status={request.status}
        type={request.type}
        isBusy={actions.isBusy || run.isRunning}
        isObservationOpen={observation.isOpen}
        hasIntegrationFailure={hasIntegrationFailure}
        onGoToCommerce={() => navigate(`/business/commerce/${request.commerceId}/dashboard`)}
        onEdit={() => navigate(`${INTEGRATION_REQUESTS_PATH}/${request.id}/edit`)}
        onCancelRequest={actions.cancel}
        onObserve={observation.open}
        onIntegrate={run.runAll}
        onRetryIntegration={run.retry}
        onApproveDoc={actions.approveDoc}
        onMarkSent={actions.markSent}
      />
      {confirmation.dialog}
    </>
  );
};

export default IntegrationRequestDetail;
